
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function HousingPage() {
  const amenities = [
    {
      icon: 'ri-home-4-line',
      title: 'Private & Shared Rooms',
      description: 'Choose from private or shared accommodations based on availability and preference'
    },
    {
      icon: 'ri-wifi-line',
      title: 'Utilities & Wi-Fi',
      description: 'Electricity, water, heating, and high-speed internet included'
    },
    {
      icon: 'ri-t-shirt-line',
      title: 'Laundry Access',
      description: 'On-site laundry available 24/7 for residents'
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Shared Kitchen & Living',
      description: 'Fully equipped kitchens and comfortable common areas for connection and community'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety Standards',
      description: 'Secure properties with cleanliness and safety maintained daily'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Prime Locations',
      description: 'Close to public transportation, jobs, and healthcare services'
    }
  ];

  const eligibilityRequirements = [
    {
      icon: 'ri-medal-line',
      title: 'Veteran Status',
      description: 'Valid DD-214 required for verification',
      required: true
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Active Income',
      description: 'Steady income or benefits covering housing costs',
      required: true
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Independent Living',
      description: 'Ability to live independently and follow house guidelines',
      required: true
    },
    {
      icon: 'ri-team-line',
      title: 'Community Respect',
      description: 'Commitment to maintaining a respectful, supportive environment',
      required: false
    }
  ];

  const housingTypes = [
    {
      title: 'Private Room',
      price: '$1200-1500/month',
      features: [
        'Private bedroom with lock',
        'Shared bathroom (2-3 residents)',
        'Personal storage space',
        'All utilities included',
        'Access to furnished common areas'
      ],
      image: 'https://public.readdy.ai/ai/img_res/8440c30d3cb4fb806e05291b187a67ac.jpg'
    },
    {
      title: 'Shared Room',
      price: '$800-1200/month',
      features: [
        'Shared bedroom with roommate',
        'Shared bathroom facilities',
        'Personal storage locker',
        'All utilities included',
        'Community living environment'
      ],
      image: 'https://readdy.ai/api/search-image?query=Shared%20bedroom%20for%20veteran%20housing%20with%20two%20single%20beds%2C%20clean%20organized%20space%2C%20personal%20storage%20areas%2C%20comfortable%20and%20respectful%20living%20arrangement%2C%20modern%20furnishings&width=400&height=300&seq=shared-room&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=Modern%20veteran%20housing%20complex%20exterior%2C%20clean%20residential%20buildings%20with%20American%20flag%2C%20well-maintained%20property%20with%20landscaping%2C%20professional%20architecture%2C%20safe%20neighborhood%20setting%2C%20welcoming%20entrance&width=1920&height=800&seq=housing-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Quality Homes for Independent Living
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
            Safe, affordable, fully furnished housing designed for veterans rebuilding independence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/apply">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                Apply for Housing
              </Button>
            </Link>
            <Link to="/housing">
              <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-white text-white hover:bg-white hover:text-gray-900">
                View Available Homes
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Housing Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Housing Options
            </h2>
            <p className="text-lg text-gray-600">
              Choose the living arrangement that best fits your needs and budget
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {housingTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url('${type.image}')` }}
                ></div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{type.title}</h3>
                  <div className="text-2xl font-bold text-blue-600 mb-4">{type.price}</div>
                  <ul className="space-y-2">
                    {type.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-600">
                        <i className="ri-check-line text-green-600 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-600 italic">
              Note: Rates vary by location and availability.
            </p>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What's Included
            </h2>
            <p className="text-lg text-gray-600">
              Every home comes with essential amenities for comfortable living
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {amenities.map((amenity, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${amenity.icon} text-blue-600 text-xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{amenity.title}</h3>
                <p className="text-gray-600">{amenity.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Requirements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Eligibility Requirements
            </h2>
            <p className="text-lg text-gray-600">
              Simple requirements to ensure our community remains safe and supportive
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {eligibilityRequirements.map((requirement, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${requirement.icon} text-blue-600 text-xl`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{requirement.title}</h3>
                    {requirement.required && (
                      <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">{requirement.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Benefits */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Conveniently Located for Stability and Access
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Our properties are carefully selected for proximity to essential services, 
                employment opportunities, and transportation networks.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <i className="ri-bus-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Public Transit</div>
                    <div className="text-blue-200 text-sm">Close to bus and metro stops</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <i className="ri-briefcase-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Employment</div>
                    <div className="text-blue-200 text-sm">Near major employers and job centers</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <i className="ri-shopping-cart-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Shopping</div>
                    <div className="text-blue-200 text-sm">Grocery stores and essentials within minutes</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <i className="ri-hospital-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Healthcare</div>
                    <div className="text-blue-200 text-sm">Close to VA clinics and hospitals</div>
                  </div>
                </div>
              </div>
            </div>
            <div 
              className="h-96 bg-cover bg-center rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Texas%20neighborhood%20with%20veteran%20housing%2C%20clean%20residential%20area%20with%20good%20transportation%20access%2C%20modern%20buildings%2C%20safe%20community%20environment%2C%20urban%20planning%2C%20accessible%20location&width=600&height=400&seq=location-benefits&orientation=landscape')`
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Apply?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Our fast-track application process places most veterans within 24–48 hours of approval.
          </p>
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Required Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div className="flex items-center space-x-3">
                <i className="ri-file-text-line text-blue-600"></i>
                <span className="text-gray-700">DD-214 Discharge Papers</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-money-dollar-circle-line text-blue-600"></i>
                <span className="text-gray-700">Proof of Income or Benefits</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-id-card-line text-blue-600"></i>
                <span className="text-gray-700">Valid Photo ID</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/apply">
              <Button size="lg" className="w-full sm:w-auto">
                Start Application
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                Ask a Question
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Help With Housing?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Our team is available to answer questions and help guide your application.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:3462918220" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              Call (346) 291-8220
            </a>
            <a href="mailto:Fastfundingcapital@yahoo.com" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              Send Email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
